import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { AssignedTeamMemberState, AssignedTeamMember } from './types'
import { fetchAssignedTeamMembers } from './action'
import { selectFilteredAssignedTeamMembers } from './selector'
import { setDateFormat, setTimeFormat } from '../../../../utils/dateUtils'

export const generateReport = createAsyncThunk(
  'assignedTeamMemberPerTask/generateReport',
  async (_, { getState }) => {
    try {
      const state = getState() as any
      const taskData = state.assignedTeamMemberPerTask.data
      const filters = state.assignedTeamMemberPerTask.filters
      const filteredData = selectFilteredAssignedTeamMembers(state)?.assignedTeamMembers || []

      // Create filter string for filename
      const activeFilters = Object.entries(filters)
        .filter(([_, value]) => value !== undefined && value !== '')
        .map(([key, value]) => `${key}-${value}`)
        .join('_')

      // Convert data to CSV format
      const headers = [
        'Workday ID',
        'Name',
        'Email',
        'Functional Area',
        'Supervisor',
        'Operational Manager',
        'Registered?',
        'TIDS Practice',
        'Due Date',
        'Assigned Date',
        'Started Date',
        'Completion Date',
        'Compliance Metric',
      ]
      const formatDateTime = (value: string | null | undefined): string => {
        if (!value) return ''
        return `${setDateFormat(value)} ${setTimeFormat(value)}`
      }

      const csvData = filteredData.map((member: AssignedTeamMember) => [
        member.workdayId,
        member.fullName,
        member.email,
        member.functionalArea,
        member.supervisorName,
        member.operationalManagerName,
        member.taskStatus.toLowerCase() === 'completed' ? 'Yes' : 'No',
        member.tidsPractice,
        formatDateTime(member.dueDate),
        formatDateTime(member.assignedDate),
        formatDateTime(member.startedDate),
        formatDateTime(member.completionDate),
        member.complianceMetric,
      ])

      // Helper function to escape CSV values
      const escapeCsvValue = (value: string | null | undefined): string => {
        if (value === null || value === undefined) {
          return ''
        }
        const stringValue = String(value)
        // If the value contains comma, quote, or newline, wrap it in quotes and escape internal quotes
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`
        }
        return stringValue
      }

      // Create CSV content with proper escaping
      const csvContent = [
        headers.map(escapeCsvValue).join(','),
        ...csvData.map((row: (string | null | undefined)[]) => 
          row.map(escapeCsvValue).join(',')
        )
      ].join('\n')

      // Create Blob and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Create filename using task title, ID, and filters
      const fileName = `${taskData.taskTitle.replace(/[^a-zA-Z0-9]/g, '_')}_${taskData.taskId}${activeFilters ? '_' + activeFilters : ''}_assigned_members_report.csv`
      link.setAttribute('download', fileName)

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      return true
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error('Error generating CSV report:', error.message)
      } else {
        console.error('Unknown error generating CSV report')
      }
      throw error
    }
  },
)

const initialState: AssignedTeamMemberState = {
  data: null,
  loading: false,
  error: null,
  filters: {
    taskStatus: undefined,
    searchQuery: undefined,
    jobProfile: undefined,
    functionalArea: undefined,
    supervisorName: undefined,
    operationalManagerName: undefined,
    tidsPractice: undefined,
  },
}

const assignedTeamMemberSlice = createSlice({
  name: 'assignedTeamMemberPerTask',
  initialState,
  reducers: {
    clearAssignedTeamMembers: (state) => {
      state.data = null
      state.error = null
    },
    setAssignedTeamMembers: (state, action: PayloadAction<AssignedTeamMember[]>) => {
      if (state.data) {
        state.data.assignedTeamMembers = action.payload
      } else {
        state.data = {
          taskId: '',
          taskTitle: '',
          targetCompleted: 0,
          totalAssigned: action.payload.length,
          assignedTeamMembers: action.payload,
        }
      }
    },
    setTaskStatusFilter: (state, action) => {
      state.filters.taskStatus = action.payload
    },
    setSearchQuery: (state, action) => {
      state.filters.searchQuery = action.payload
    },
    setJobProfileFilter: (state, action) => {
      state.filters.jobProfile = action.payload
    },
    setFunctionalAreaFilter: (state, action) => {
      state.filters.functionalArea = action.payload
    },
    setSupervisorNameFilter: (state, action) => {
      state.filters.supervisorName = action.payload
    },
    setOperationalManagerNameFilter: (state, action) => {
      state.filters.operationalManagerName = action.payload
    },
    setTidsPracticeFilter: (state, action) => {
      state.filters.tidsPractice = action.payload
    },
    clearFilters: (state) => {
      state.filters = initialState.filters
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssignedTeamMembers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAssignedTeamMembers.fulfilled, (state, action) => {
        state.loading = false
        state.data = action.payload
        state.error = null
      })
      .addCase(fetchAssignedTeamMembers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(generateReport.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(generateReport.fulfilled, (state) => {
        state.loading = false
        state.error = null
      })
      .addCase(generateReport.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const {
  clearAssignedTeamMembers,
  setAssignedTeamMembers,
  setTaskStatusFilter,
  setSearchQuery,
  setJobProfileFilter,
  setFunctionalAreaFilter,
  setSupervisorNameFilter,
  setOperationalManagerNameFilter,
  setTidsPracticeFilter,
  clearFilters,
} = assignedTeamMemberSlice.actions

export default assignedTeamMemberSlice.reducer
